import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { TranslateService } from '@ngx-translate/core';
import { ALERT_TYPE, TEXT_AREA_STATE } from "../../providers/myria/app-variables";
import { DeeplinkProvider } from '../../providers/marketing/deeplink';
import { RouteProvider } from "../../providers/route/route";
import { LoggerEvent, LoggerProvider } from "../../providers/logger/logger";
import { UtilsProvider } from "../../providers/myria/utils-provider";
import { trigger, style, transition, animate } from '@angular/animations';
import { IterableInAppProvider } from "../../providers/marketing/iterable-inapp";
import { CarouselCardType, CarouselCTAEntryPoint, SegmentLocationNames } from "../../model/types/analytics.const";
import { AnalyticsEventType, MobileInboxAccessLocations } from '../../model/types/analytics.const';
import { Slides } from "ionic-angular";
import * as $ from 'jquery'
import { ScriptProvider } from "../../providers/script";
import { SegmentProvider } from '../../providers/analytics/segment';

declare var Swiper;

@Component({
  selector: "rmt-slides",
  templateUrl: "rmt-slides.html",
  animations: [
    trigger('visibilityChanged', [
      transition(':leave', [
        style({transform: 'fadeIn', opacity: 1}),
        animate('100ms', style({transform: 'fadeOut', opacity: 0}))
      ])
    ])
  ]
})
export class RmtSlides {
  public swiper: any;
  @ViewChild(Slides) slides: Slides;
  @ViewChild('rmtslides') rmtslides;
  @Input() slidesMessages?: any = [];
  @Input() showViewAllButton?: boolean = false;
  @Input() isHandleSwipdeSlideToLastVisibleIndex?: boolean = false;
  @Input() onClickCloseCb?: any;
  @Input() onClickItemCb?: any;
  @Input() slidesPerView = 'auto';
  @Input() spaceBetween = 12;
  @Input() slidesLoop = false;
  @Input() ellipsisTitle = false;
  @Input() isMessagesSlides = false;
  @Output() clickSlide = new EventEmitter<any>();
  @Output() clickActionButton = new EventEmitter<any>();
  static removedSlideArray = [];
  
  messagesArray: any;
  clickClosed: boolean = false;
  actionButtonClicked: boolean = false;
  currentIndexPos: number = 0;
  swiperInitialized: boolean = false;
  slideToRemove: number = undefined;
  nextSlideToRemove: number = undefined;
  lastSlideToRemove: number = undefined;
  isHideAlertParent: boolean = false;
  removedSlideTimeout: any;
  animationTimeout: any;
  buttonClickedTimout: any;
  slidesVisible = false;
  viewAllText: string = '';

  constructor(
    private scriptProvider: ScriptProvider,
    private translate: TranslateService,
    private routeProvider: RouteProvider,
    private deeplinkProvider: DeeplinkProvider,
    private utilsProvider: UtilsProvider,
    private iterableInAppProvider: IterableInAppProvider,
    private analytics: SegmentProvider,
  ) {
  }

  ngOnInit() {
    this.viewAllText = this.translate.instant('ActivityPage.ViewAllText');
  }

  ngAfterViewInit() {
    if(!this.swiper) {
      this.initializeSwiper();
    }
  }

  ngOnDestroy() {
    if(this.removedSlideTimeout)
      window.clearTimeout(this.removedSlideTimeout);

    if(this.animationTimeout)
      window.clearTimeout(this.animationTimeout);

    if(this.buttonClickedTimout)
      window.clearTimeout(this.buttonClickedTimout);
  }

  async initializeSwiper() {
    try {
      this.swiper = new Swiper(this.rmtslides.nativeElement, {
        slidesPerView: this.slidesPerView,
        spaceBetween: this.spaceBetween,
        loop: this.slidesLoop,
        speed: 500,
      });
      this.swiper.on('slideChange', (ev) => {
        this.handleSlideChange(ev);
      });
      this.updateSwiper();
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, "Failed to initialize swiper");
    }
  }

  public updateSwiper() {
    // important it needs to be delayed so Angular can first render the new slides and then we have to call swiper update
    // otherwise it could happen that we call update and then Angular overwrites it by rerendering the slides
    window.requestAnimationFrame(() => {
      this.swiper.update();
      this.swiper.slidesPerViewDynamic();
      this.slidesVisible = true;
    });
    window.setTimeout(() => {
      this.swiper.update();
      this.swiper.slidesPerViewDynamic();
      this.slidesVisible = true;
    }, 250);
  }

  onClickSlide(inAppMessage: any, idx: number) {
    // A CTA tap bubbles up here; that is a CTA click, not a card tap.
    if(this.isMessagesSlides && !this.actionButtonClicked) {
      if (inAppMessage.alertType) {
        this.trackCarouselCardClickedEvent(
          inAppMessage.titleKey ? this.utilsProvider.getTranslationInEnglish(inAppMessage.titleKey) : '',
          '',
          CarouselCardType.Alert,
          '',
          '',
          idx
        );
      } else {
        this.trackCarouselCardClickedEvent(
          '',
          inAppMessage.campaignId ? inAppMessage.campaignId : '',
          CarouselCardType.Iterable,
          this.getCustomPayloadValue(inAppMessage, 'deeplinkURL'),
          inAppMessage.messageId ? inAppMessage.messageId : '',
          idx
        );
      }
    }

    if(this.clickClosed || this.actionButtonClicked)
      return;
    const messageId = inAppMessage.messageId;
    const messageRead = inAppMessage.read;
    const inboxMetadata = inAppMessage.inboxMetadata ? inAppMessage.inboxMetadata : '';
    if(inAppMessage.content && inAppMessage.content.payload && inAppMessage.content.payload.isDeeplink) {
      this.parseTargetURL(inAppMessage.content.payload.deeplinkURL);
    } else if(inAppMessage.content && inAppMessage.content.payload && inAppMessage.content.payload.showDetailMessage) {
      this.routeProvider.openModal(this.routeProvider.pages.IterablesInappNotificationPage, {htmlContent: inAppMessage.content.html, messageId: messageId, messageRead: messageRead, inboxMetadata: inboxMetadata, campaignId: inAppMessage.campaignId, lineNumber: idx, entryPoint: CarouselCTAEntryPoint.Carousel});
    }
    inAppMessage.lineNumber = idx;
    this.clickSlide.emit(inAppMessage);
  }

  getCustomPayloadValue(slideMessage: any, key: string): string {
    return slideMessage.customPayload && slideMessage.customPayload[key] ? slideMessage.customPayload[key] : '';
  }

  getSlideIndex(slideMessage: any): number {
    return this.slidesMessages.indexOf(slideMessage) + 1;
  }

  trackCarouselCardClickedEvent(alertName: string, campaignId: string, cardType: CarouselCardType, deepLink: string, messageId: string, idx: number) {
    this.analytics.trackEvent({
      eventName: AnalyticsEventType.CarouselCardClicked,
      analyticsData: {
        alertName: alertName,
        campaignId: campaignId,
        carouselCardType: cardType,
        deepLink: deepLink,
        lineNumber: idx,
        messageId: messageId,
      }
    }, true);
  }

  parseTargetURL(targetUrl: string) {
    try {
      const splitURL = targetUrl.split("://");
      const stringURL = splitURL.length > 1 ? splitURL[1].trim() : "";
      const httpURLPrefix = splitURL.length > 0 ? splitURL[0].trim() : "";
      let data = {};
      let pageName = stringURL;

      if (pageName.toLowerCase() === "dismiss") {
        this.routeProvider.pop();
        return;
      }

      // Handling of action parameters, needed when in iterable workflow marketing team need to send a parameter like order id
      //, which is dependednt on the workflow of iterable. Since they cannot create JSON object dynamically and encode it into 
      //base 64. They will be sending it as a parameter
      if (httpURLPrefix === "action" && stringURL.indexOf("?") > -1) {
        const dataParamString = stringURL.substring(stringURL.indexOf("?") + 1, stringURL.length);
        const dataParam = JSON.parse('{"' + decodeURI(dataParamString.replace(/&/g, "\",\"").replace(/=/g, "\":\"")) + '"}')
        const baseString = stringURL.substring(0, stringURL.indexOf("?"));
        this.deeplinkProvider.handleCustomAction(baseString, dataParam);
        return;
      }
      else if (stringURL.indexOf("?") > -1) {
        pageName = stringURL.substring(0, stringURL.indexOf("?"));
        const params = (new URL(targetUrl)).searchParams;
        data = this.utilsProvider.paramsToObject(params);
        this.deeplinkProvider.processDeeplink({ page: pageName, data: JSON.stringify(data) });
        return;
      }


      this.deeplinkProvider.handleCustomAction(stringURL);
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, 'Unable to parse URL');
    }
  }

  getInApMessageIcon(inAppMessage) {
    return inAppMessage.inboxMetadata.icon;
  }

  getImageSrc(slideMessage) {
    let imagePath = "../assets/imgs/warn_logo_carousel.svg";
    if(slideMessage.image) {
       imagePath = slideMessage.image;
    } else if(slideMessage.alertType) {
        imagePath = this.getAlertImagePath(slideMessage);
    } else if(slideMessage.content && slideMessage.content.payload && slideMessage.content.payload.img) {
      imagePath = slideMessage.content.payload.img;
    }
    return imagePath;
  }

  getLabelStatusClass(slideMessage) {
    let alertStyle;
    switch (slideMessage.alertType) {
      case ALERT_TYPE.ALERT_INFO:
       alertStyle = "alert-info";
        break;
      case ALERT_TYPE.ALERT_SUCCESS:
       alertStyle = "alert-success";
        break;
      case ALERT_TYPE.ALERT_WARNING:
       alertStyle = "alert-warn";
        break;
      case ALERT_TYPE.ALERT_ERROR:
      case ALERT_TYPE.ALERT_IMPORTANT:
       alertStyle = "alert-error";
        break;
      default:
        alertStyle = "alert-info";
        break;
    }
    return alertStyle;
  }

  getAlertImagePath(slideMessage) {
    let imagePath;
    switch (slideMessage.alertType) {
      case ALERT_TYPE.ALERT_INFO:
        imagePath = "assets/imgs/info_logo_carousel.svg";
        break;
      case ALERT_TYPE.ALERT_SUCCESS:
        imagePath = "assets/imgs/success_logo_carousel.svg";
        break;
      case ALERT_TYPE.ALERT_WARNING:
        imagePath = "assets/imgs/warn_logo_carousel.svg";
        break;
      case ALERT_TYPE.ALERT_ERROR:
        imagePath = "assets/imgs/error_logo_carousel.svg";
        break;
      case ALERT_TYPE.PREPAID_CARD:
        imagePath = "assets/imgs/prepaid_cards_slides.png";
        break;
      case ALERT_TYPE.ALERT_SHIELD:
        imagePath = "assets/imgs/alert-shield.svg";
        break;
      case ALERT_TYPE.ALERT_IMPORTANT:
        imagePath = "assets/imgs/important_logo_carousel.svg";
        break;
      default:
        imagePath = "assets/imgs/info_logo_carousel.svg";
        break;
    }
    return imagePath;
  }

  processButton(ev, slideMessage) {
    if(this.buttonClickedTimout)
      window.clearTimeout(this.buttonClickedTimout);

    this.actionButtonClicked = true;

    // Track before parseTargetURL navigates away.
    const buttonAction = this.getCustomPayloadValue(slideMessage, 'buttonAction');
    if(this.isMessagesSlides && buttonAction) {
      this.iterableInAppProvider.trackCTAClick({
        cardType: CarouselCardType.Iterable,
        url: buttonAction,
        campaignId: slideMessage.campaignId,
        lineNumber: this.getSlideIndex(slideMessage),
        messageId: slideMessage.messageId,
        entryPoint: CarouselCTAEntryPoint.Carousel,
      });
    }

    if(slideMessage.actionButton) {
      this.clickActionButton.emit(slideMessage);
    }
    
    if(slideMessage.customPayload && slideMessage.customPayload.buttonAction) {
      this.parseTargetURL(slideMessage.customPayload.buttonAction);
    }

    this.buttonClickedTimout = window.setTimeout(() => this.actionButtonClicked = false, 75);
  }

  openHyperLink(slideMessage) {
    if(slideMessage.hyperlinkAction) {
      slideMessage.hyperlinkAction();
    }
  }

  removeSlideBtn(index: number): void {
    this.clickClosed = true;
    const removedSlide = this.slidesMessages[index];
    if(this.removedSlideTimeout)
      window.clearTimeout(this.removedSlideTimeout);

    if(this.animationTimeout)
      window.clearTimeout(this.animationTimeout);

    this.slideToRemove = index;
    if (index + 1 < this.slidesMessages.length) {
      this.nextSlideToRemove = index + 1;
    }
    if(index === this.slidesMessages.length - 1 && this.slidesMessages.length == 1) {
      this.lastSlideToRemove = index;
      this.hideSlideDivWithAnimation();
    }
    
    this.removedSlideTimeout = setTimeout(() => {
      this.slidesMessages.splice(index, 1);
      if (this.slidesMessages.length == 0) {
        this.hideSlideCard(index)
      }
      this.clickClosed = false;
    }, 500);

    if(this.slidesMessages.length > 1) {
      this.hideSlideCard(index);
    }
    
    RmtSlides.removedSlideArray.push(removedSlide);
    if(removedSlide && removedSlide.messageId) {
      this.iterableInAppProvider.postInAppMessageOpen(removedSlide.messageId)
    }
    this.onClickCloseCb(removedSlide);
  }

  hideSlideCard(index) {
    this.animationTimeout = setTimeout(() => {
      if (this.swiper) {
        this.swiper.removeSlide(index); // Remove the slide from Swiper
        this.slideToRemove = undefined;
        this.nextSlideToRemove = undefined;
        this.lastSlideToRemove = undefined
        this.swiper.slideTo(index, 1200); 
        this.swiper.update();
      }
    }, 150);
  }

  hideSlideDivWithAnimation() {
    const height = $(this.rmtslides.nativeElement.parentNode).outerHeight();
    this.rmtslides.nativeElement.style.height = parseInt(height.toString()) + 'px';
    this.rmtslides.nativeElement.style.transition = '0.2s linear height';
    window.setTimeout(() => {
      $(this.rmtslides.nativeElement).addClass('slide-up');
      window.setTimeout(()=> {
        this.isHideAlertParent = true;
      }, 100)
    }, 300);
  }

  clickInAppMessageViewAll() {
    this.iterableInAppProvider.openMobilAppInbox(null, MobileInboxAccessLocations.HomeScreenCarousel);
  }

  getMessageMarginClassTop(inAppMessage) {
    if (inAppMessage.isClosable) {
      return 'margin-top-alert-close';
    } else if (inAppMessage.content && inAppMessage.content.payload && inAppMessage.content.payload.isClosable) {
      return 'margin-top-alert-inapp'
    }
  }

  getCountryIcon(value) {
    if (value)
      return 'assets/countryFlagSvg/' + value.toUpperCase() + '.svg';
    return '';
  }

  handleSlideChange(ev) {
    if (!this.isHandleSwipdeSlideToLastVisibleIndex) {
      if (this.slidesMessages.length > 0 && this.slidesMessages[this.slidesMessages.length - 1].emptySlide) {
      this.swiper.slideTo(0);
      }
    } else {
      if (this.slidesMessages.length > 0 && this.slidesMessages[this.slidesMessages.length - 1].emptySlide) {
        const visibleSlides = this.swiper.slidesPerView;
        const lastVisibleIndex = this.slidesMessages.length - visibleSlides;
  
        if (this.swiper.activeIndex > lastVisibleIndex) {
          this.swiper.slideTo(lastVisibleIndex);
        }
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/rmt-slides/rmt-slides.ts